import {
    combineLatest as observableCombineLatest,
    combineLatest,
    empty as observableEmpty,
    Observable,
    of as observableOf,
    of,
    Subject, switchMap
} from 'rxjs';

import {map, mergeMap, take, tap} from 'rxjs/operators';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input
} from '@angular/core';
import {Device} from '../../types/devices/device';
import {DeviceService} from '../../services/device/device.service';
import {DeviceId, ExpeditionId, ProjectId, UserId} from '../../types/ids';
import {IResourceSummaryComponent} from '../resources/resource-summary.component';
import {Router} from '@angular/router';
import {WebmateEvent} from '../../types/base-types';
import {Browser, browserFromBackendObject} from '../../types/browser';
import {UserService} from '../../services/user.service';
import {
    AccessPackagesPermission,
    CreateOrDeleteDevicePermission,
    ManageSlotsPermission,
    ModifyDevicePermission,
    OpenDeviceConsolePermission,
    RedeployDevicePermisson,
    SetDeviceSlotProperties
} from '../../constants/permission.constants';
import {DeviceSlotService} from '../../services/device/device-slot.service';
import {DeviceSlot} from '../../types/devices/device-slot';
import {UserInfo} from '../../types/user/user-info';
import {DeployableService} from '../../services/device/deployable.service';
import {FeatureService} from '../../services/feature.service';
import {DeviceSlotReservation, reservationSortingFunction} from '../../types/devices/device-slot-reservation';
import {isDefined} from '../../utils/utils';
import {VirtualPackage} from '../../types/packages/package';
import {BandwidthLimit} from '../../types/interfaces/bandwidth-limit.interface';
import {bandwidthLimitConfigurations, packetLossConfigurations} from '../../constants/quality-of-service.constants';
import {WmNotificationService} from '../notification/wm-notification.service';
import {PacketLoss} from '../../types/interfaces/packet-loss.interface';
import {AcceptBiometricAuthentication, SimulateBiometrics} from '@webmate-web-core/shared/constants/capability.constants';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {
    BrowsersessionViewerComponent,
    BrowsersessionViewerToolConfig
} from '@webmate-web-core/shared/tools/browsersessionviewer/browsersessionviewer.component';
import {DeviceSim} from '@webmate-web-core/shared/types/devices/device-sim';
import {DeviceProperties, DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {LogoResolverService} from '@webmate-web-core/shared/services/logo-resolver.service';
import {
    AppInstallationComponent,
    AppInstallationToolConfig
} from '@webmate-web-core/shared/components/device/appinstallation/app-installation.component';
import {faTv} from '@fortawesome/pro-light-svg-icons';
import {faPlug} from '@fortawesome/pro-regular-svg-icons';
import {
    DeviceMalfunctionReporterComponent,
    DeviceMalfunctionReporterToolConfig
} from '@webmate-web-core/shared/tools/malfunctionreporter/device-malfunction-reporter.component';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {User} from '@webmate-web-core/shared/types/user/user';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {capitalize} from '@webmate-web-core/shared/utils/utils';
import {Deployable} from '@webmate-web-core/shared/types/devices/device-template';
import * as _ from 'lodash';
import {ModalToolContainerService} from '../../tools/modaltoolcontainer/modaltoolcontainer.service';
import {ConsoleAccessService} from '../../services/device/console-access.service';
import {
    SeleniumCapabilityGeneratorComponent,
    SeleniumCapabilityGeneratorToolConfig
} from '@webmate-web-core/shared/tools/capability-generator/selenium-capability-generator.component';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {UpdateRequestToolComponent, UpdateRequestToolConfig} from '@webmate-web-core/shared/tools/updaterequest/update-request.component';
import {WorkbenchOpenService} from '@webmate-web-core/shared/workbench-ipc';
import {Popover} from 'primeng/popover';
import {AdvancedOptionsPopoverCoordinatorService} from './advanced-options-popover-coordinator.service';
import {DeviceDetailsComponent, DeviceDetailsToolConfig} from './device-details.component';

interface DevicePropertyDetail {
    fullKey: string;
    key: string;
    value: unknown;
}

interface DevicePropertyGroup {
    name: string;
    entries: DevicePropertyDetail[];
}

interface AdvancedAction {
    id: string;
    label: string;
    icon: IconProp;
    disabled?: boolean;
    danger?: boolean;
    textButton?: boolean;
    keepOpen?: boolean;
    bandwidthAction?: boolean;
    command: () => void;
}

@UntilDestroy()
@Component({
    selector: 'wm-device-summary',
    templateUrl: './device-summary.component.html',
    styleUrls: ['./device-summary.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceSummaryComponent implements IResourceSummaryComponent {

    Array = Array; // TODO when WMKNBN-9441 is fixed
    faTv = faTv;
    faPlug = faPlug;
    bandwidthOptionsVisible = false;
    DevicePropertyNames = DevicePropertyNames;

    genericEvent: Subject<WebmateEvent>;

    @Input() resourceType: string;
    @Input() resourceId: DeviceId;
    @Input() projectId: ProjectId;
    @Input() belongsToDifferentProject: boolean = false;
    @Input() viewType: string;
    @Input() params: any;

    currentUser: User;

    device?: Device = undefined;
    deviceDetailsVisible: boolean = false;
    deviceCreator: UserInfo;
    deviceSlot?: DeviceSlot = undefined; //note that a queued Device has no Slot!
    slotLoaded = false;
    browsersOfDevice?: Array<Browser> = undefined;
    properties?: DeviceProperties = undefined;
    logoSrc?: string = undefined;
    visibleToJobs: boolean = false;
    deployable?: Deployable = undefined;

    hasAccessToBandwidthLimiting = false;

    //TODO: temporary, remove
    userMaySetPacketLoss = false;

    hasSimcard: boolean;
    siminfo: Array<DeviceSim> = [];

    biometricSimulationActive = false;
    acceptBiometricAuthentication = false;

    bandwidthLimit?: BandwidthLimit;
    bandwidthLimitConfigurations: BandwidthLimit[] = bandwidthLimitConfigurations;
    packetLoss?: PacketLoss;
    packetLossConfigurations: PacketLoss[] = packetLossConfigurations;
    disconnected: boolean = false;

    exclusiveConsole: boolean = true;

    reservations?: Array<DeviceSlotReservation>;
    shownReservation?: DeviceSlotReservation;
    userHavingReservation?: UserInfo;
    isReservedByAnotherUser: boolean = true; //the sane default is true!
    isReservedByMyself: boolean = false;
    wasCreatedByMyself: boolean = false;

    isAccessedByDifferentUser: boolean = true; //the sane default is true!
    otherUsers: Array<UserId> = [];
    otherUserInfo: Array<UserInfo> = [];
    isAutomaticallyInUse: boolean = false;
    wasDeployedManually: boolean = false;
    isBusy: boolean = true;
    isInstalling: boolean = false;
    isDeleting: boolean = true;

    isJobToggling: boolean = false;

    mayAccessConsole: boolean = false;
    mayDelete: boolean = false;
    mayModifyDevice: boolean = false;
    mayRedeploy: boolean = false;
    mayToggleDailyRedeployment: boolean = false;
    mayClearSlotsNoMatterWhat: boolean = false;
    mayInstrumentApp: boolean = false;
    mayAccessPackages: boolean = false;
    mayOpenDeviceConsolePermission: boolean = false;
    mayReserveSlot: boolean = false;
    mayRequestDeviceUpdate = false;

    packageManagementFeaturesEnabled = false;
    instrumentationEnabled = false;

    hasReconnectProperty: boolean = false;
    hasInstallPackagesProperty: boolean = false;
    hasCreateSnapshotProperty: boolean = false;
    hasRestoreSnapshotProperty: boolean = false;
    hasRevertToInitialProperty: boolean = false;
    hasResetProperty: boolean = false;


    oldBrowserSessions: Array<BrowserSessionInfo>;
    activeBrowserSession?: BrowserSessionInfo;

    reload$: Subject<void> = new Subject<void>();

    advancedActions: AdvancedAction[] = [];
    bandwidthMenuConfigurations = bandwidthLimitConfigurations.toReversed();

    constructor(private deviceService: DeviceService, private deviceSlotService: DeviceSlotService,
                private router: Router,
                private browserSessionService: BrowserSessionService,
                private platformLogoResolver: LogoResolverService,
                private featureService: FeatureService, private userService: UserService,
                private notificationService: WmNotificationService,
                private modalToolService: ModalToolContainerService,
                private cdRef: ChangeDetectorRef,
                private deployableService: DeployableService,
                private readonly workbenchOpenService: WorkbenchOpenService,
                private advancedOptionsPopoverCoordinator: AdvancedOptionsPopoverCoordinatorService,
                private consoleAccessService: ConsoleAccessService) {
    }

    private buildDropdownMenu() {
        if (!this.device) {
            return;
        }
        this.advancedActions = [];

        if (this.hasCreateSnapshotProperty) {
            this.advancedActions.push({
                id: `device-${this.device.id}-create-snapshot`,
                label: 'Create snapshot',
                icon: 'camera',
                disabled: this.belongsToDifferentProject || this.isReservedByAnotherUser || this.isBusy || this.isAutomaticallyInUse || !this.mayModifyDevice,
                command: this.snapshot.bind(this)
            });
            this.advancedActions.push({
                id: `device-${this.device.id}-revert-to-snapshot-btn`,
                label: 'Revert to snapshot',
                icon: 'history',
                disabled: this.belongsToDifferentProject || this.isReservedByAnotherUser || this.isBusy || this.isAutomaticallyInUse || !this.mayModifyDevice || !this.hasRestoreSnapshotProperty,
                command: this.revertToSnapshot.bind(this)
            });
        }

        if (this.hasReconnectProperty) {
            this.advancedActions.push({
                id: `device-${this.device.id}-reconnect-redeploy-btn`,
                label: 'Reconnect device',
                icon: 'link',
                textButton: true,
                disabled: this.belongsToDifferentProject || this.isReservedByAnotherUser || this.isBusy || this.isAutomaticallyInUse || !this.mayRedeploy || !this.wasDeployedManually,
                command: this.reconnect.bind(this)
            });
        } else {
            // Redeploy moved here from the top-level toolbar to keep the desktop
            // layout aligned with the mobile version until a suitable mobile action exists.
            this.advancedActions.push({
                id: `device-${this.device.id}-reconnect-redeploy-btn`,
                label: 'Redeploy',
                icon: 'sync-alt',
                textButton: true,
                disabled: this.belongsToDifferentProject || this.isReservedByAnotherUser || this.isBusy || this.isAutomaticallyInUse || !this.mayRedeploy || !this.wasDeployedManually,
                command: this.redeploy.bind(this)
            });
        }

        if (this.hasResetProperty) {
            this.advancedActions.push({
                id: `device-${this.device.id}-resetbtn`,
                label: 'Reset device',
                icon: 'undo-alt',
                disabled: this.belongsToDifferentProject || this.isReservedByAnotherUser || this.isAutomaticallyInUse || !this.mayModifyDevice || !this.wasDeployedManually,
                command: this.reset.bind(this)
            });
        }

        if (this.hasRevertToInitialProperty) {
            this.advancedActions.push({
                id: `device-${this.device.id}-resetbtn`,
                label: 'Reset device',
                icon: 'undo-alt',
                disabled: this.device.state !== 'running' || this.belongsToDifferentProject || (this.isAccessedByDifferentUser && this.exclusiveConsole) || this.isReservedByAnotherUser,
                command: this.revertToInitial.bind(this)
            });
        }

        if (!this.isReservedByMyself && this.mayClearSlotsNoMatterWhat && (this.belongsToDifferentProject || this.isReservedByAnotherUser || this.isDeleting || !this.mayDelete)) {
            this.advancedActions.push({
                id: `device-${this.device.id}-claim-slot-btn`,
                label: 'Force Device Release',
                icon: faPlug,
                command: this.clearSlot.bind(this)
            });
        }

        if (this.hasInstallPackagesProperty) {
            this.advancedActions.push({
                id: `device-${this.device.id}-installappbtn`,
                label: 'Install app',
                icon: 'download',
                disabled: !this.mayModifyDevice || !this.mayAccessPackages || this.device.state !== 'running' || this.belongsToDifferentProject || (this.isAccessedByDifferentUser && this.exclusiveConsole) || this.isReservedByAnotherUser,
                command: this.showAppInstallationModal.bind(this)
            });
        }

        if (this.showBandwidthOptions) {
            this.advancedActions.push({
                id: `device-${this.device.id}-bandwidth-btn`,
                label: 'Bandwidth',
                icon: 'signal',
                keepOpen: true,
                textButton: true,
                bandwidthAction: true,
                command: () => this.bandwidthOptionsVisible = !this.bandwidthOptionsVisible
            });
        }

        this.advancedActions.push({
            id: `device-${this.device.id}-show-capabilities-btn`,
            label: 'Show capabilities',
            icon: 'code',
            textButton: true,
            command: this.showCapabilityGenerator.bind(this)
        });

        this.advancedActions.push({
            id: `device-${this.device.id}-detailsbtn`,
            label: 'Show Details',
            icon: 'info-circle',
            textButton: true,
            command: this.openDeviceDetails.bind(this)
        });

        this.advancedActions.push({
            id: `device-${this.device.id}-report-malfunction-btn`,
            label: 'Report malfunction',
            icon: 'exclamation-triangle',
            textButton: true,
            command: this.openDeviceMalfunctionReportModal.bind(this)
        });

        if (this.mayRequestDeviceUpdate && this.deviceSlot?.isPrivateMobile()) {
            const updatePending = this.deviceSlot?.propertyBag?.updatePending === true;
            this.advancedActions.push({
                id: `device-${this.device.id}-request-update-btn`,
                label: updatePending ? 'Update already requested' : 'Request update',
                icon: 'arrow-circle-up',
                disabled: updatePending,
                command: this.openRequestUpdateModal.bind(this)
            });
        }

        if (this.isReservedByMyself) {
            this.advancedActions.push({
                id: `device-${this.device.id}-claim-slot-btn`,
                label: 'Claim Slot',
                icon: 'hand-paper',
                textButton: true,
                command: this.clearSlot.bind(this)
            });
        }
    }

    get automationToggleDisabled(): boolean {
        return this.belongsToDifferentProject || this.isReservedByAnotherUser || this.isAutomaticallyInUse || !this.mayModifyDevice || this.isJobToggling;
    }

    get showDailyRedeploymentToggle(): boolean {
        return this.deviceSlot !== undefined && !this.deviceSlot.isPublicSlot();
    }

    get dailyRedeploymentEnabled(): boolean {
        return this.deviceSlot?.isAutoRedeployEnabledForProject(this.projectId) ?? false;
    }

    get dailyRedeploymentToggleDisabled(): boolean {
        return this.belongsToDifferentProject || this.isReservedByAnotherUser || !this.mayToggleDailyRedeployment;
    }

    get showBandwidthOptions(): boolean {
        return this.mayModifyDevice && this.hasAccessToBandwidthLimiting && this.deviceSlot !== undefined;
    }

    get bandwidthSelectionLabel(): string {
        if (this.disconnected) {
            return 'Offline';
        }
        return this.bandwidthLimit?.limit ?? 'No limit';
    }

    get showPacketLossOptions(): boolean {
        return this.showBandwidthOptions && this.userMaySetPacketLoss;
    }

    selectBandwidthLimit(config?: BandwidthLimit): void {
        if (this.disconnected) {
            this.disconnect(false);
        }
        if (this.bandwidthLimit?.apiName !== config?.apiName) {
            this.limitBandwidth(config);
        }
    }

    selectOfflineBandwidth(): void {
        if (!this.disconnected) {
            this.disconnect(true);
        }
    }

    alignAdvancedOptionsArrow(popover: Popover, trigger: HTMLElement): void {
        const container = popover.container;
        if (!container) {
            return;
        }

        const containerBounds = container.getBoundingClientRect();
        const triggerBounds = trigger.getBoundingClientRect();
        const triggerCenter = triggerBounds.left + triggerBounds.width / 2 - containerBounds.left;
        const arrowLeft = Math.max(12, Math.min(containerBounds.width - 12, triggerCenter));
        container.style.setProperty('--device-advanced-options-arrow-left', `${arrowLeft}px`);
    }

    toggleAdvancedOptions(event: Event, popover: Popover, trigger: HTMLElement): void {
        this.advancedOptionsPopoverCoordinator.activate(popover);
        popover.toggle(event, trigger);
    }

    closeAdvancedOptions(popover: Popover): void {
        this.bandwidthOptionsVisible = false;
        this.advancedOptionsPopoverCoordinator.release(popover);
    }

    executeAdvancedAction(action: AdvancedAction, popover: Popover): void {
        if (!action.keepOpen) {
            popover.hide();
        }
        action.command();
    }

    hideBandwidthOptions(): void {
        this.bandwidthOptionsVisible = false;
    }

    isConsoleAccessible() {
        return this.consoleAccessService.isConsoleAccessible({
            mayAccessConsole: this.mayAccessConsole,
            belongsToDifferentProject: this.belongsToDifferentProject,
            isAccessedByDifferentUser: this.isAccessedByDifferentUser,
            exclusiveConsole: this.exclusiveConsole,
            isReservedByAnotherUser: this.isReservedByAnotherUser,
            hasAnnotations: this.hasAnnotations,
            annotationStatus: this.annotationStatus,
        });
    }

    setupDone() {
        return this.device?.properties[DevicePropertyNames.SetupDone] !== undefined
            && this.device.properties[DevicePropertyNames.SetupDone] === true;
    }

    private providerSpecificBusy() {
        //TODO: support more Providers!
        return this.device?.properties[DevicePropertyNames.VCloudState] !== undefined &&
            this.device.properties[DevicePropertyNames.VCloudState] !== 'on';
    }

    private providerSpecificDeleting() {
        //TODO: support more Providers!
        return this.device?.properties[DevicePropertyNames.VCloudState] !== undefined &&
            (this.device.properties[DevicePropertyNames.VCloudState] === 'deleting' || this.device.properties[DevicePropertyNames.VCloudState] === 'deleted');
    }

    private updateIsBusy(skipCD: boolean = false) {
        this.isBusy = (this.device === undefined || this.device.state !== 'running' || this.providerSpecificBusy() || this.isInstalling);
        this.isDeleting = (this.device === undefined || this.device.state === 'releasing' || this.providerSpecificDeleting());

        if (!skipCD) {
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
        }
    }

    ngOnInit() {
        /** Load Features*/
        let ssoFeature = this.featureService.getFeature('webmate.console.single-user-only').pipe(tap(f => this.exclusiveConsole = f?.isActive));
        let bandwidthFeature = this.featureService.getFeature('webmate.network-simulation').pipe(tap(feature => {
            if (isDefined(feature) && feature.isActive) {
                this.hasAccessToBandwidthLimiting = true;
            }
        }));
        let packageFeature = this.featureService.getFeature('webmate.package-management').pipe(tap(feature => {
            this.packageManagementFeaturesEnabled = feature?.isActive;
            this.instrumentationEnabled = feature['instrumentationEnabled'];
            this.mayInstrumentApp = this.packageManagementFeaturesEnabled && this.instrumentationEnabled;
        }));
        let reservationFeature = this.featureService.getFeature('webmate.device-slot-reservations').pipe(tap(f => {
            if (f?.isActive) {
                this.mayReserveSlot = true;
            }
        }));
        let deviceUpdateFeature = this.featureService.getFeature('webmate.request-device-update').pipe(tap(f => {
            if (f?.isActive) {
                this.mayRequestDeviceUpdate = true;
            }
        }));
        let features$ = combineLatest([ssoFeature, bandwidthFeature, packageFeature, reservationFeature, deviceUpdateFeature]).pipe(take(1));

        /** obtain some permissions for Operations */
        let packageAccessPermission = this.userService.hasPermissionInProject(this.projectId, AccessPackagesPermission).pipe(tap(permission => {
            this.mayAccessPackages = permission;
        }));
        let setDevicePropertiesPermission = this.userService.hasPermissionInProject(this.projectId, SetDeviceSlotProperties).pipe(tap(yesOrNo => {
            this.mayToggleDailyRedeployment = yesOrNo;
        }));
        let openConsolePermission = this.userService.hasPermissionInProject(this.projectId, OpenDeviceConsolePermission).pipe(tap(permission => this.mayOpenDeviceConsolePermission = permission));
        let manageSlotPermission = this.userService.hasAdminPermission(ManageSlotsPermission).pipe(tap(yesOrNo => this.mayClearSlotsNoMatterWhat = yesOrNo));
        let modifyDevicePermission = this.userService.hasPermissionInProject(this.projectId, ModifyDevicePermission).pipe(tap(
            permission => {
                this.mayModifyDevice = permission;
            }
        ));
        let redeployPermission = this.userService.hasPermissionInProject(this.projectId, RedeployDevicePermisson).pipe(tap(permission => this.mayRedeploy = permission));
        let createOrDeletePermission = this.userService.hasPermissionInProject(this.projectId, CreateOrDeleteDevicePermission).pipe(tap(permission => this.mayDelete = permission));

        let permissions$ = combineLatest([packageAccessPermission, setDevicePropertiesPermission, openConsolePermission, manageSlotPermission, modifyDevicePermission, redeployPermission, createOrDeletePermission]);

        /** obtain the actual device */
        features$.pipe(
            switchMap(() => permissions$),
            switchMap(() => this.userService.getCurrentUserData()),
            switchMap(u => {
                this.currentUser = u;
                return this.deviceService.getDevice$(this.resourceId);
            }), switchMap(next => {
                this.device = next; //update device
                delete this.device.properties["clipboard.lastKnownContent"];
                this.updateIsBusy(true);
                this.browsersOfDevice = next.properties[DevicePropertyNames.MachineBrowsers] !== undefined ? next.properties[DevicePropertyNames.MachineBrowsers].map((b: any) => browserFromBackendObject(b)) : [];
                this.properties = next.properties;
                //TODO: proper representation of unknown platform
                if (this.logoSrc === undefined && next.platform !== undefined && next.platform.platformType !== 'UNKNOWN' && next.properties !== undefined) { //ask the AssetAndBlobService for a representation of the Device if we have one
                    this.platformLogoResolver.getLogoForDevice(this.device).subscribe(logo => {
                        this.logoSrc = logo;
                        this.cdRef.markForCheck();
                    });
                }
                this.visibleToJobs = this.device.isAvailableToJobs();
                this.biometricSimulationActive = next.properties[SimulateBiometrics];
                this.acceptBiometricAuthentication = next.properties[AcceptBiometricAuthentication];
                this.hasCreateSnapshotProperty = next.properties[DevicePropertyNames.StateCreateSnapshot] === true;
                this.hasRestoreSnapshotProperty = next.properties[DevicePropertyNames.StateRevertToSnapshot] === true;
                this.hasRevertToInitialProperty = next.properties[DevicePropertyNames.StateRevertToInitial] === true;
                this.wasDeployedManually = next.creationTicket.job?.properties?.isAutomatedJob !== true;
                this.hasSimcard = next.properties[DevicePropertyNames.OpenSTFSimCardInfo] !== undefined;
                this.siminfo = next.properties[DevicePropertyNames.OpenSTFSimCardInfo] as Array<DeviceSim>;
                this.hasReconnectProperty = next.properties[DevicePropertyNames.StateReconnect] === true;
                this.hasResetProperty = next.properties[DevicePropertyNames.StateReset] === true;
                this.hasInstallPackagesProperty = next.properties[DevicePropertyNames.CanInstallPackages] === true;
                this.isAutomaticallyInUse = next.properties[DevicePropertyNames.ActiveVehicles]?.length > 0;

                this.wasCreatedByMyself = next.creationTicket.userId == this.currentUser.id;
                this.mayAccessConsole = this.mayOpenDeviceConsolePermission && next.properties[DevicePropertyNames.Console];

                let bandwidthLimit = next.properties[DevicePropertyNames.SimulateBandwidth];
                if (isDefined(bandwidthLimit) && bandwidthLimit !== 'default') {
                    this.bandwidthLimit = this.bandwidthLimitConfigurations.find(conf => conf.apiName === bandwidthLimit);
                } else {
                    this.bandwidthLimit = undefined;
                }

                let packetLoss = next.properties[DevicePropertyNames.SimulatePaketLoss];
                if (isDefined(packetLoss) && packetLoss !== '0%') {
                    this.packetLoss = this.packetLossConfigurations.find(conf => conf.apiName === packetLoss);
                } else {
                    this.packetLoss = undefined;
                }

                this.disconnected = !!next.properties[DevicePropertyNames.SimulateDisconnect];

                this.isAccessedByDifferentUser = false;
                this.otherUsers = [];
                this.otherUserInfo = [];
                let userInfoObservable: Observable<UserInfo>[] = [];
                next.tickets.forEach(ticket => {
                    if (ticket.job.name === 'DeviceSession') {
                        return;
                    }
                    //TODO: properly display usage by multiple users and automatic usage here
                    if (ticket.job.name === 'ConsoleAccess' && ticket.userId !== this.currentUser.id) {
                        this.isAccessedByDifferentUser = true;
                        if (this.otherUsers.indexOf(ticket.userId) < 0) {
                            this.otherUsers.push(ticket.userId);
                            //this means the Users are not sorted, but what the hell...    too costly for such a minor UseCase
                            userInfoObservable.push(this.userService.getInformationAboutUser(ticket.userId));
                        }
                    }
                    if (ticket.job.properties.isAutomatedJob) {
                        this.isAutomaticallyInUse = true;
                    }
                });
                return (userInfoObservable.length > 0 ? observableCombineLatest(userInfoObservable) : observableOf([]));
            }), switchMap(userInfos => {
                this.otherUserInfo = userInfos as UserInfo[];
                return this.userService.getInformationAboutUser(this.device!.creationTicket.userId);
            }), switchMap(userInfo => {
                this.deviceCreator = userInfo;
                if (this.device?.slot) {
                    return this.deviceSlotService.getSlotInProject$(this.device.slot, this.projectId);
                } else {// device is probably queued, anyway without a Slot it cannot be reserved by someone else!
                    this.isReservedByAnotherUser = false;
                    return observableEmpty(/* mimics behavior of deviceSlotService.getSlotForDeviceInProject */);
                }
            }), switchMap(slot => {
                this.slotLoaded = true;
                this.deviceSlot = slot;
                if (this.mayReserveSlot) {
                    this.reservations = slot.reservations;
                } else {
                    this.reservations = [];
                }
                let activeReserv = this.reservations!.find(r => r.isActive);
                this.shownReservation = activeReserv ?? this.reservations!.sort(reservationSortingFunction)[0];
                this.isReservedByAnotherUser = activeReserv !== undefined && this.currentUser.id !== activeReserv.userId;
                this.isReservedByMyself = activeReserv !== undefined && this.currentUser.id === activeReserv.userId;

                return this.deployableService.getDeployablesForSlot$(this.deviceSlot.id);
            }), switchMap(deployables => {
                this.deployable = deployables.find(d => d.properties[DevicePropertyNames.MachineId] === this.device!.properties[DevicePropertyNames.MachineId]);
                return (this.shownReservation !== undefined) ? this.userService.getInformationAboutUser(this.shownReservation.userId) : of(undefined);
            }), switchMap(userHavingReservations => {
                let result;
                if (this.setupDone() && !!this.device) {
                    result = this.deviceService.getBrowserSessionsForDevice(this.device.id);
                } else {
                    result = of([]);
                }
                return result.pipe(map(sessions => ({sessions, userHavingReservations})));
            }), tap(({sessions}) => {
                this.activeBrowserSession = sessions.find((b: BrowserSessionInfo) => b.isActive());
                this.oldBrowserSessions = _.sortBy(sessions.filter((b: BrowserSessionInfo) => !b.isActive()), s => s.optStart);
            }), untilDestroyed(this))
            .subscribe(result => {
                // result as any because rxjs pipes throw away type after 9 pipe elements m-(
                this.userHavingReservation = (result as any).userHavingReservations as UserInfo;
                this.buildDropdownMenu();
                this.cdRef.markForCheck();
            });

        this.reload$.next(undefined);

    }

    stopExpedition(expeditionId: ExpeditionId) {
        this.browserSessionService.stopExpedition$(expeditionId).pipe(take(1)).subscribe(_ => {
            this.cdRef.markForCheck();
        });
    }

    openBrowserSessionViewer(sessionId: ExpeditionId) {
        this.modalToolService.openTool(BrowsersessionViewerComponent, new BrowsersessionViewerToolConfig(sessionId));
    }

    getAutoRedeployStatus(): string {
        return this.deviceSlot !== undefined && this.device !== undefined ? (this.deviceSlot.isAutoRedeployEnabledForProject(this.device.projectId) ? 'redeployed daily' : 'no daily redeployment') : '...';
    }

    getAccessUsers(): string {
        let base = 'Device currently accessed by ';
        if (this.otherUserInfo.length == 1) {
            return base + this.otherUserInfo[0].name;
        } else {
            let msg = base + this.otherUserInfo.map(userInfo => userInfo.name).join(', ');
            let pos = msg.lastIndexOf(',');
            return msg.substring(0, pos) + ' and' + msg.substring(pos + 1);
        }
    }

    getUserNameForMobileBridgeAccess(userId: string): Observable<string> {
        return this.userService.getInformationAboutUser(userId).pipe(map(u => `Used for mobile debugging by ${u.name}`));
    }

    access() {
        if (!this.isConsoleAccessible()) {
            return;
        }
        let request = () => {
            this.isBusy = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();

            this.workbenchOpenService.openWorkbench({
                projectId: this.projectId,
                deviceId: this.resourceId,
            }, {
                blockedPopupMessage: 'Your browser has blocked this console, please allow pop-ups / new tabs for this webpage!'
            }).pipe(take(1)).subscribe();
        };
        if (this.isAutomaticallyInUse) {
            this.notificationService.open('This device is currently used by an automated test. Do you really want to access it? ', 'Yes', request, 'No');
        } else if (this.isAccessedByDifferentUser) {
            this.notificationService.open('This device is currently accessed by another user. Do you really want to access it?', 'Yes', request, 'No');
        } else {
            request();
        }
    }

    snapshot() {
        if (this.belongsToDifferentProject || this.isBusy || this.isAutomaticallyInUse || !this.mayModifyDevice || this.isReservedByAnotherUser) {
            return;
        }
        let request = () => {
            this.isBusy = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceService.requestSnapshotOfDevice$(this.resourceId).subscribe(
                next => {
                } /* nothing, wait for event */,
                err => {
                    this.notificationService.toastWarningNotification('Requesting snapshot for device failed!');
                    this.updateIsBusy();
                }
            );
        };
        if (this.isAccessedByDifferentUser) {
            this.notificationService.open('This device is currently accessed by another user. Do you really want to take a snapshot of it, the device might not be responsive during this operation?', 'Take snapshot', request, 'Cancel');
        } else {
            request();
        }
    }

    revertToSnapshot() {
        if (this.belongsToDifferentProject || this.isBusy || this.isAutomaticallyInUse || !this.mayModifyDevice || this.isReservedByAnotherUser || !this.hasRestoreSnapshotProperty) {
            return;
        }
        let request = () => {
            this.isBusy = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceService.requestRevertToSnapshot$(this.resourceId).subscribe(
                next => {
                }/* nothing, wait for event */,
                err => {
                    this.notificationService.toastWarningNotification('Request for resetting to snapshot failed');
                    this.updateIsBusy();
                }
            );
        };
        this.notificationService.open('Resetting this device will restore the exact state of the last snapshot. You will loose all changes made since then, is this what you want?' + (this.isAccessedByDifferentUser ? ' NOTE: Another user is currently working with this device!' : ''), 'Yes', request, 'No');
    }

    revertToInitial() {
        if (this.belongsToDifferentProject || this.isBusy || this.isAutomaticallyInUse || !this.mayModifyDevice || this.isReservedByAnotherUser || !this.hasRevertToInitialProperty) {
            return;
        }
        let request = () => {
            this.isBusy = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceService.requestRevertToInitial$(this.resourceId).pipe(mergeMap(() => {
                return this.deviceService.handleDeviceRevertedToInitialEvent();
            }), take(1)).subscribe(result => {
                if (result.deviceId === this.device?.id) {
                    if (result.errorMessage) {
                        this.notificationService.toastErrorNotification(result.errorMessage);
                    }
                    this.isBusy = false;
                    this.buildDropdownMenu();
                    this.cdRef.markForCheck();
                }
            });

        };
        this.notificationService.open('Resetting this device will remove apps installed by users and reset settings. You will loose all changes. Is this what you want?' + (this.isAccessedByDifferentUser ? ' NOTE: Another user is currently working with this device!' : ''), 'Yes', request, 'No');
    }

    setBiometricSimulation(value: boolean) {
        //TODO: proper implementation with more reasonable behaviour, remove hardcoded caps and create a map somewhere
        // this.deviceService.setDeviceCapability(this.resourceId, [new DeviceCapability(SimulateBiometrics, value), new DeviceCapability(AcceptBiometricAuthentication, true)]);
        // this.biometricSimulationActive = value;
    }

    redeploy() {
        if (this.belongsToDifferentProject || this.isBusy || this.isAutomaticallyInUse || !this.mayRedeploy || this.isReservedByAnotherUser || !this.wasDeployedManually) {
            return;
        }
        let request = () => {
            this.isBusy = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceService.requestRedeployment(this.resourceId).subscribe(
                next => {
                } /* nothing, wait for event */
            );
        };
        //TODO: proper messages depending on device configuration
        this.notificationService.open('Redeploying this device will delete the current device and deploy a fresh one with an identical configuration. You will lose all changes made since the time of deployment and all running jobs and console sessions will be terminated, is this what you want?' + (this.isAccessedByDifferentUser ? ' NOTE: Another user is currently working with this device!' : ''), 'Yes', request, 'No');
    }


    release() {
        if (this.belongsToDifferentProject || this.isDeleting || !this.mayDelete || (this.isReservedByAnotherUser && !this.wasCreatedByMyself)) return;
        let request = () => {
            this.isBusy = true;
            this.isDeleting = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceService.requestReleaseOfDevice$(this.resourceId).subscribe({
                next: _ => /* nothing, wait for event */ {
                },
                error: err => {
                    this.notificationService.toastWarningNotification('Request for device release failed!');
                    this.updateIsBusy();
                }
            });
        };
        if (this.isAutomaticallyInUse) {
            this.notificationService.open('WARNING: You are about to release a device which is currently running an automated test! This will terminate all running sessions.', 'Release Device', request, 'Cancel');
        } else {
            this.notificationService.open('Do you really want to release this device? This will terminate all running console sessions.' + (this.isAccessedByDifferentUser ? ' NOTE: Another user is currently working with this device!' : ''), 'Release Device', request, 'Cancel');
        }
    }

    reconnect() {
        if (this.belongsToDifferentProject || this.isDeleting || !this.mayDelete || (this.isReservedByAnotherUser && !this.wasCreatedByMyself)) return;
        let request = () => {
            this.isBusy = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceService.requestReconnectOfDevice$(this.resourceId).subscribe({
                next: next => {
                } /* nothing, wait for event */,
                error: err => {
                    this.notificationService.toastWarningNotification('Request for reconnect failed!');
                    this.updateIsBusy();
                }
            });
        };
        if (this.isAutomaticallyInUse) {
            this.notificationService.open('WARNING: You are about to reconnect a device which is currently running an automated test! This will terminate all running sessions.', 'Reconnect Device', request, 'Cancel');
        } else {
            this.notificationService.open('Do you really want to reconnect this device? This will terminate all console sessions.' + (this.isAccessedByDifferentUser ? ' NOTE: Another user is currently working with this device!' : ''), 'Reconnect Device', request, 'Cancel');
        }
    }

    reset() {
        let request = () => {
            this.isBusy = true;
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceService.requestResetOfDevice$(this.resourceId).subscribe({
                next: next => {/* nothing, wait for event */
                },
                error: err => {
                    this.notificationService.toastWarningNotification('Request for device reset failed!');
                    this.updateIsBusy();
                }
            });

        };
        if (this.isAutomaticallyInUse) {
            this.notificationService.open('WARNING: You are about to reset a device which is currently running an automated test! This will terminate all running sessions.', 'Reset Device', request, 'Cancel');
        } else {
            this.notificationService.open('Resetting this device will interrupt all running sessions and wipe all user-data and user-installed apps on the device. Continue?' + (this.isAccessedByDifferentUser ? ' NOTE: Another user is currently working with this device!' : ''), 'Reset Device', request, 'Cancel');
        }
    }

    readonly renameDevice = (newValue: string): Observable<boolean> =>
        this.deviceService.requestRenameOfDevice$(this.resourceId, newValue).pipe(tap({
            next: () => {
                this.device!.name = newValue;
                this.cdRef.markForCheck();
            },
            error: () => this.notificationService.toastWarningNotification('Request for device rename failed!')
        }));

    setUsageByAutomatedJobs(value: boolean) {
        if (this.isAutomaticallyInUse || !this.mayModifyDevice || this.isReservedByAnotherUser) return;
        this.isJobToggling = true;
        this.cdRef.markForCheck();
        this.deviceService.requestVisibilityToJobsChange$(this.resourceId, value).subscribe(
            next => {
                this.isJobToggling = false;
                this.cdRef.markForCheck();
            },
            err => {
                this.isJobToggling = false;
                this.notificationService.toastWarningNotification('An unknown error occurred when trying to update the device\'s visibility for automation/tests.');
                this.visibleToJobs = !value;
                this.buildDropdownMenu();
                this.cdRef.markForCheck();
            }
        );
    }

    toggleDeviceDetails() { /* since Details are totally passive, we do allow opening multiple ones at the same time */
        this.deviceDetailsVisible = !this.deviceDetailsVisible;
        this.cdRef.markForCheck();
    }

    openDeviceDetails(): void {
        if (!this.device) {
            return;
        }
        this.modalToolService.openTool(DeviceDetailsComponent, new DeviceDetailsToolConfig(
            this.device,
            this.browsersOfDevice ?? [],
            this.deviceCreator,
            this.activeBrowserSession,
            this.oldBrowserSessions ?? []
        ));
    }

    closeDeviceDetails() {
        this.deviceDetailsVisible = false;
        this.cdRef.markForCheck();
    }

    hasIPInfo(): boolean {
        return this.getIPInfo() !== undefined;
    }

    getIPInfo(): string | undefined {
        if (this.deviceSlot === undefined) {
            return undefined;
        }

        /** check the permanent bag first */
        if (this.deviceSlot.propertyBag.permanent_properties !== undefined) {
            if (this.deviceSlot.propertyBag.permanent_properties['ip'] !== undefined) {
                return this.deviceSlot.propertyBag.permanent_properties['ip'];
            }
        }
        if (this.deviceSlot.propertyBag.temporary_properties !== undefined) {
            if (this.deviceSlot.propertyBag.temporary_properties['ip'] !== undefined) {
                return this.deviceSlot.propertyBag.temporary_properties['ip'];
            }
        }
        return undefined;
    }

    toggleAutoRedeploymentForSlot() {
        if (this.belongsToDifferentProject || !this.mayToggleDailyRedeployment || this.deviceSlot === undefined || this.isReservedByAnotherUser) return;
        this.deviceSlotService.updateAutoRedeploymentOfSlotInProject(this.projectId, this.deviceSlot.id, !this.deviceSlot.isAutoRedeployEnabledForProject(this.projectId)).pipe(take(1)).subscribe();
    }

    clearSlot(): void {
        let request = () => {
            if (this.deviceSlot === undefined) return;
            this.deviceSlotService.clearSlot(this.deviceSlot.id).subscribe({
                next: () => {/* nothing, wait for event */
                },
                error: err => this.notificationService.toastWarningNotification('Clearing/Claiming the slot ' + (this.deviceSlot?.name || 'n/a') + ' failed due to an unexpected error')
            });
        };
        this.notificationService.open(this.isReservedByMyself ? 'You have a reservation for this slot now but someone else is using it. You can enforce your reservation now, thereby stopping and deleting the device of the other user. Continue?' : 'As an administrator, you can forcefully clear this slot, deleting whatever device is currently contained within. Note that this might disrupt the workflow of other users. Continue?', this.isReservedByMyself ? 'Claim Slot' : 'Clear Slot', request, 'Cancel');
    }

    disconnect(disconnected: boolean): void {
        this.deviceService.setDisconnected(this.device!.id, disconnected).subscribe(success => {
            this.disconnected = disconnected;
            this.cdRef.markForCheck();
        }, err => console.warn(err));
    }

    limitBandwidth(config?: BandwidthLimit): void {
        if (config) {
            this.deviceService.setBandwidthLimit(this.device!.id, config.apiName).subscribe(success => {
                this.bandwidthLimit = config;
                this.cdRef.markForCheck();
            }, err => console.warn(err));
        } else {
            this.deviceService.setBandwidthLimit(this.device!.id, 'default').subscribe(success => {
                this.bandwidthLimit = undefined;
                this.cdRef.markForCheck();
            }, err => console.warn(err));
        }
    }

    simulatePacketLoss(config?: PacketLoss): void {
        if (config) {
            this.deviceService.setPacketLoss(this.device!.id, config.apiName).subscribe(success => {
                this.packetLoss = config;
                this.cdRef.markForCheck();
            }, err => console.warn(err));
        } else {
            this.deviceService.setPacketLoss(this.device!.id, '0%').subscribe(success => {
                this.packetLoss = undefined;
                this.cdRef.markForCheck();
            }, err => console.warn(err));
        }
    }

    showCapabilityGenerator() {
        this.modalToolService.openTool(SeleniumCapabilityGeneratorComponent, new SeleniumCapabilityGeneratorToolConfig(this.projectId, this.device));
    }

    showAppInstallationModal() {
        let triggeredAppInstallationCallback = (virtualPackage: VirtualPackage, instrumented: boolean) => {
            if (!this.device) {
                return;
            }
            this.modalToolService.closeLastTool();

            this.isInstalling = true;
            this.updateIsBusy();

            this.deviceService.installAppOnDevice$(this.device!.id, virtualPackage, instrumented).pipe(untilDestroyed(this)).subscribe({
                // TODO: for multiple simultaneous installations, this is broken right now, but that use case is not really important
                next: result => {
                    this.isInstalling = false;
                    this.updateIsBusy();

                    if (result.errorMessage) {
                        this.notificationService.toastErrorNotification(result.errorMessage);
                    } else {
                        this.notificationService.toastNotification(`Successfully installed app on device ${this.device!.name}.`);
                    }
                },
                error: () => {
                    this.isInstalling = false;
                    this.updateIsBusy();
                    this.notificationService.toastWarningNotification(`Could not confirm the app installation on device ${this.device!.name}. Please check the device.`);
                }
            });
        };
        if (!!this.device?.platform) {
            this.modalToolService.openTool(AppInstallationComponent, new AppInstallationToolConfig(this.device.platform, this.projectId, triggeredAppInstallationCallback));
        }
    }

    //TODO maybe build pipetransform if we want to reuse this
    prettyPrintDetailValue(value: any) {
        switch (typeof value) {
            case 'number':
            case 'string':
            case 'boolean':
                return `${value}`;
            default:
                return JSON.stringify(value);
        }
    }

    shortId(id: string): string {
        return id.length > 11 ? `${id.slice(0, 9)}...${id.slice(-2)}` : id;
    }

    getGroupedDeviceProperties(): DevicePropertyGroup[] {
        if (this.device?.properties === undefined) {
            return [];
        }
        const groups = new Map<string, DevicePropertyGroup>();
        Object.keys(this.device.properties).sort().forEach(fullKey => {
            const separatorIndex = fullKey.indexOf('.');
            const groupName = separatorIndex > 0 ? fullKey.substring(0, separatorIndex) : 'General';
            const key = separatorIndex > 0 ? fullKey.substring(separatorIndex + 1) : fullKey;
            let group = groups.get(groupName);
            if (group === undefined) {
                group = {name: groupName, entries: []};
                groups.set(groupName, group);
            }
            group.entries.push({
                fullKey,
                key,
                value: this.device!.properties[fullKey]
            });
        });
        return Array.from(groups.values());
    }

    isObjectValue(value: any) {
        return value !== null && typeof value === 'object';
    }

    openDeviceMalfunctionReportModal() {
        this.modalToolService.openTool(DeviceMalfunctionReporterComponent, new DeviceMalfunctionReporterToolConfig(this.resourceId, this.projectId));
    }

    openRequestUpdateModal() {
        const serial = this.device!.properties[DevicePropertyNames.OpenSTFSerial];
        const currentVersion = this.deployable!.platform.getFullName();
        const modelName = this.deployable!.modelName;
        this.modalToolService.openTool(UpdateRequestToolComponent, new UpdateRequestToolConfig(this.deviceSlot!, serial, modelName, currentVersion, this.projectId));
    }

    get hasAnnotations() {
        return !!this.deployable?.properties[DevicePropertyNames.Annotations];
    }

    get annotationStatus() {
        return this.deployable?.properties[DevicePropertyNames.Annotations]?.['status'];
    }

    get annotationString() {
        let msg = this.deployable?.properties[DevicePropertyNames.Annotations]['msg'];
        return capitalize(this.annotationStatus) + (!!msg ? ': ' + msg : '');
    }

    get hasUserAnnotations() {
        return !!this.deployable?.properties[DevicePropertyNames.UserAnnotations];
    }

    get userAnnotationString() {
        return this.deployable?.properties[DevicePropertyNames.UserAnnotations]['msg'];
    }

    generateConsoleAccessTooltip() {
        if (this.isConsoleAccessible())
            return 'Open workbench for this device';
        else
            return 'Workbench is currently unavailable';
    }
}
